import React from 'react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { Shield, Mail, Phone, Lock, Eye, Database, Users, Clock, FileText } from 'lucide-react';

export default function Confidentialite() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-emerald-100 p-3 rounded-xl">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Politique de confidentialité
              </h1>
              <p className="text-slate-500 mt-1">
                Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            {/* Préambule */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-blue-900 font-semibold mb-2">Protection de vos données</p>
              <p className="text-blue-800 leading-relaxed">
                TrésoPilot s'engage à protéger la vie privée de ses utilisateurs. Cette politique explique comment nous 
                collectons, utilisons et protégeons vos données personnelles conformément au Règlement Général sur la 
                Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>
            </div>

            {/* 1. Responsable du traitement */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">1. Responsable du traitement</h2>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Éditeur :</strong> TrésoPilot</p>
                <p><strong>Statut :</strong> Micro-entreprise</p>
                <p><strong>SIRET :</strong> 952 476 950 00011</p>
                <p><strong>Siège social :</strong> 60 rue François Ier, 75008 Paris, France</p>
                <p><strong>Email :</strong> <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a></p>
                <p><strong>WhatsApp Business :</strong> +33 7 56 90 15 80</p>
                <p className="pt-2 border-t border-slate-200 mt-4">
                  <strong>Service :</strong> TrésoPilot est un service de pilotage de trésorerie et de suivi des échéances 
                  destiné aux micro-entrepreneurs français, proposant des estimations de cotisations sociales, d'impôts et de 
                  charges, ainsi que des rappels d'échéances URSSAF, TVA et CFE.
                </p>
              </div>
            </section>

            {/* 2. Données collectées */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">2. Données collectées</h2>
              </div>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">2.1 Données fournies directement par l'Utilisateur</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li><strong>Identité et contact :</strong> Email, nom, prénom (si fournis), identifiants de connexion</li>
                <li><strong>Paramètres d'activité :</strong>
                  <ul className="list-circle pl-6 mt-1 space-y-1">
                    <li>Périodicité de déclaration URSSAF (mensuelle ou trimestrielle)</li>
                    <li>Type d'activité (prestations de services, vente de marchandises, activité mixte)</li>
                    <li>Options fiscales et sociales : ACRE (exonération partielle), versement libératoire de l'impôt sur le revenu, 
                    franchise en base de TVA ou assujettissement à la TVA</li>
                    <li>Taux personnalisés (URSSAF, impôt, TVA), montant CFE estimé</li>
                  </ul>
                </li>
                <li><strong>Données financières saisies :</strong>
                  <ul className="list-circle pl-6 mt-1 space-y-1">
                    <li>Chiffre d'affaires encaissé (montants et périodes)</li>
                    <li>Charges et dépenses (si saisies)</li>
                    <li>Catégories, enveloppes virtuelles, objectifs d'épargne</li>
                    <li>Notes et commentaires libres</li>
                  </ul>
                </li>
                <li><strong>Correspondance support :</strong> Messages, questions et pièces jointes envoyés au service client</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-6">2.2 Données liées aux paiements</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Les paiements sont traités par <strong>Stripe</strong>, prestataire de services de paiement sécurisé certifié PCI-DSS. 
                TrésoPilot ne collecte ni ne stocke vos coordonnées bancaires complètes (numéros de carte).
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>Identifiants techniques Stripe : customer_id, subscription_id, session_id</li>
                <li>Statut d'abonnement (actif, expiré, annulé)</li>
                <li>Historique des transactions, reçus et factures</li>
                <li>Informations de facturation : nom, email, adresse (si fournis à Stripe)</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-6">2.3 Données collectées automatiquement</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>Adresse IP, logs de connexion</li>
                <li>Dates et heures d'accès, pages consultées, actions effectuées</li>
                <li>Informations techniques : type d'appareil, navigateur, système d'exploitation, résolution d'écran</li>
                <li>Données d'usage : fonctionnalités utilisées, fréquence de connexion</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-6">2.4 Cookies et traceurs</h3>
              <p className="text-slate-700 leading-relaxed">
                Nous utilisons des cookies pour assurer le bon fonctionnement du Service et améliorer votre expérience. 
                Pour plus de détails, consultez notre <a href="/cookies" className="text-emerald-600 hover:text-emerald-700">Politique Cookies</a>.
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6 mt-2">
                <li><strong>Cookies strictement nécessaires :</strong> Authentification, sécurité, préférences de session</li>
                <li><strong>Cookies analytiques :</strong> Statistiques d'audience, amélioration du service (LISTE DES OUTILS ANALYTICS : PLACEHOLDER)</li>
                <li><strong>Cookies marketing :</strong> Si activés, personnalisation et publicité (LISTE DES OUTILS MARKETING : PLACEHOLDER)</li>
              </ul>
            </section>

            {/* 3. Finalités */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">3. Finalités du traitement</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                Vos données sont collectées et traitées pour les finalités suivantes :
              </p>

              <ul className="space-y-3 text-slate-700">
                <li>
                  <strong className="text-slate-900">Fourniture du Service :</strong>
                  <ul className="list-circle pl-6 mt-1 space-y-1">
                    <li>Gestion de votre compte utilisateur et authentification</li>
                    <li>Affichage du tableau de bord et des statistiques personnalisées</li>
                    <li>Calcul des estimations de cotisations sociales, d'impôts, de TVA et de CFE selon vos paramètres</li>
                    <li>Génération du calendrier d'échéances URSSAF, TVA et CFE</li>
                    <li>Gestion des enveloppes virtuelles et objectifs d'épargne</li>
                    <li>Exports PDF et CSV (plans payants)</li>
                  </ul>
                </li>
                <li>
                  <strong className="text-slate-900">Gestion des abonnements et paiements :</strong> Facturation, renouvellement, 
                  accès aux fonctionnalités premium, gestion des résiliations
                </li>
                <li>
                  <strong className="text-slate-900">Communications transactionnelles :</strong> Emails de confirmation d'inscription, 
                  de paiement, de modification d'abonnement, de support
                </li>
                <li>
                  <strong className="text-slate-900">Rappels d'échéances :</strong> Envoi de notifications in-app et d'emails de 
                  rappel (si activés par l'utilisateur et selon le plan souscrit) pour anticiper les dates de déclaration et de paiement
                </li>
                <li>
                  <strong className="text-slate-900">Support client :</strong> Réponse à vos questions, assistance technique, 
                  traitement des réclamations
                </li>
                <li>
                  <strong className="text-slate-900">Sécurité et prévention des fraudes :</strong> Détection d'activités suspectes, 
                  protection contre les accès non autorisés
                </li>
                <li>
                  <strong className="text-slate-900">Amélioration du Service :</strong> Analyses statistiques anonymisées, tests, 
                  développement de nouvelles fonctionnalités
                </li>
                <li>
                  <strong className="text-slate-900">Obligations légales :</strong> Conservation des données de facturation, 
                  réponse aux réquisitions judiciaires
                </li>
              </ul>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Nature indicative des estimations</p>
                <p className="text-amber-800 leading-relaxed">
                  Les calculs et estimations fournis par TrésoPilot (montants à mettre de côté, dates d'échéances) sont 
                  <strong> indicatifs</strong> et basés sur les paramètres que vous avez renseignés. Ils ne constituent pas 
                  un conseil fiscal, comptable ou juridique. <strong>Vous restez responsable de vérifier vos obligations réelles 
                  auprès des organismes officiels</strong> (urssaf.fr, impots.gouv.fr) et de vous assurer de la conformité de 
                  vos déclarations et paiements.
                </p>
              </div>
            </section>

            {/* 4. Bases légales */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">4. Bases légales du traitement</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                Conformément au RGPD, chaque traitement repose sur une base légale :
              </p>

              <ul className="space-y-3 text-slate-700 list-disc pl-6">
                <li>
                  <strong>Exécution du contrat (art. 6.1.b RGPD) :</strong> Fourniture du Service, calculs, rappels, 
                  gestion du compte et des abonnements
                </li>
                <li>
                  <strong>Consentement (art. 6.1.a RGPD) :</strong> Cookies non strictement nécessaires, communications 
                  marketing facultatives (newsletters), rappels par email (si activés)
                </li>
                <li>
                  <strong>Intérêt légitime (art. 6.1.f RGPD) :</strong> Sécurité du Service, prévention des fraudes, 
                  amélioration de l'expérience utilisateur, statistiques anonymisées
                </li>
                <li>
                  <strong>Obligation légale (art. 6.1.c RGPD) :</strong> Conservation des données de facturation, 
                  réponse aux réquisitions judiciaires
                </li>
              </ul>
            </section>

            {/* 5. Destinataires */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">5. Destinataires des données</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                Vos données personnelles sont accessibles uniquement aux catégories de destinataires suivantes :
              </p>

              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li><strong>Personnel autorisé :</strong> Équipe TrésoPilot (support, développement, sécurité), dans la 
                limite de leurs attributions</li>
                <li><strong>Prestataires de services :</strong>
                  <ul className="list-circle pl-6 mt-1 space-y-1">
                    <li><strong>Hébergement :</strong> IONOS (stockage des données, hébergement web)</li>
                    <li><strong>Paiement :</strong> Stripe (traitement des transactions, gestion des abonnements)</li>
                    <li><strong>Emails transactionnels :</strong> Resend (envoi d'emails de service et de rappels)</li>
                    <li><strong>Analytics :</strong> Outils d'analyse d'audience (si activés)</li>
                  </ul>
                </li>
                <li><strong>Autorités compétentes :</strong> En cas d'obligation légale (réquisitions judiciaires, lutte 
                contre la fraude)</li>
              </ul>

              <p className="text-slate-700 leading-relaxed mt-4">
                Tous nos prestataires sont soumis à des obligations de confidentialité et de sécurité strictes. Ils ne 
                peuvent utiliser vos données que pour les finalités définies par contrat.
              </p>
            </section>

            {/* 6. Transferts hors UE */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">6. Transferts de données hors Union Européenne</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                Nous privilégions les prestataires situés dans l'Union Européenne. Toutefois, certains de nos partenaires 
                (notamment pour les services d'infrastructure et d'analyse) peuvent être basés hors de l'UE.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                Dans ce cas, nous nous assurons que des garanties appropriées sont en place conformément au RGPD :
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>Clauses contractuelles types de la Commission européenne</li>
                <li>Décisions d'adéquation de la Commission européenne (si applicable)</li>
                <li>Mesures techniques et organisationnelles supplémentaires</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-3">
                Pour plus d'informations sur ces transferts, contactez-nous à 
                <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700"> contact@tresopilot.fr</a>.
              </p>
            </section>

            {/* 7. Cookies et traceurs */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">7. Cookies et traceurs</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                TrésoPilot utilise des cookies et autres technologies de suivi pour améliorer votre expérience et analyser 
                l'utilisation du Service.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Gestion de vos préférences</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>Lors de votre première visite, une bannière vous informe de l'utilisation des cookies et vous permet 
                de gérer vos préférences</li>
                <li>Vous pouvez modifier vos choix à tout moment via le lien <strong>« Gérer mes cookies »</strong> disponible 
                dans le footer ou sur la page <a href="/cookies" className="text-emerald-600 hover:text-emerald-700">Politique Cookies</a></li>
                <li>Le refus de certains cookies peut limiter l'accès à certaines fonctionnalités du Service</li>
              </ul>

              <p className="text-slate-700 leading-relaxed mt-4">
                Pour une description complète des cookies utilisés, consultez notre 
                <a href="/cookies" className="text-emerald-600 hover:text-emerald-700"> Politique Cookies</a>.
              </p>
            </section>

            {/* 8. Durées de conservation */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">8. Durées de conservation</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                Vos données personnelles sont conservées pendant des durées proportionnées aux finalités pour lesquelles 
                elles ont été collectées :
              </p>

              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-1">Données de compte actif</p>
                  <p className="text-slate-700">
                    Conservées tant que votre compte est actif. En cas d'inactivité prolongée (absence de connexion pendant 
                    <strong> 24 mois</strong>), nous vous informons avant toute suppression ou anonymisation.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-1">Données financières saisies</p>
                  <p className="text-slate-700">
                    Conservées jusqu'à la suppression de votre compte, sauf obligations légales contraires. Les sauvegardes 
                    techniques peuvent être conservées pendant une durée limitée (30 jours maximum).
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-1">Données de facturation</p>
                  <p className="text-slate-700">
                    Conservées pendant la durée légale de <strong>5 ans</strong> à compter de la clôture de l'exercice 
                    comptable (obligations fiscales et comptables).
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-1">Logs de connexion et d'activité</p>
                  <p className="text-slate-700">
                    Conservés pendant <strong>12 mois</strong> pour des raisons de sécurité et de prévention des fraudes.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-semibold text-slate-900 mb-1">Cookies</p>
                  <p className="text-slate-700">
                    Durée maximale de <strong>13 mois</strong> pour les cookies soumis au consentement, sauf configuration 
                    spécifique indiquée dans la bannière de gestion des cookies.
                  </p>
                </div>
              </div>

              <p className="text-slate-700 leading-relaxed mt-4">
                À l'issue de ces durées, vos données sont supprimées ou anonymisées de manière irréversible.
              </p>
            </section>

            {/* 9. Vos droits RGPD */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">9. Vos droits RGPD</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants sur vos 
                données personnelles :
              </p>

              <ul className="space-y-3 text-slate-700">
                <li>
                  <strong className="text-slate-900">Droit d'accès (art. 15 RGPD) :</strong> Obtenir une copie de vos 
                  données et des informations sur leur traitement
                </li>
                <li>
                  <strong className="text-slate-900">Droit de rectification (art. 16 RGPD) :</strong> Corriger vos données 
                  inexactes ou incomplètes
                </li>
                <li>
                  <strong className="text-slate-900">Droit à l'effacement / « droit à l'oubli » (art. 17 RGPD) :</strong> 
                  Demander la suppression de vos données, sous réserve des obligations légales de conservation
                </li>
                <li>
                  <strong className="text-slate-900">Droit à la limitation du traitement (art. 18 RGPD) :</strong> Suspendre 
                  temporairement le traitement de vos données dans certaines circonstances
                </li>
                <li>
                  <strong className="text-slate-900">Droit d'opposition (art. 21 RGPD) :</strong> Vous opposer au traitement 
                  de vos données pour des raisons tenant à votre situation particulière
                </li>
                <li>
                  <strong className="text-slate-900">Droit à la portabilité (art. 20 RGPD) :</strong> Recevoir vos données 
                  dans un format structuré et couramment utilisé, ou demander leur transfert direct à un autre responsable
                </li>
                <li>
                  <strong className="text-slate-900">Droit de retirer votre consentement :</strong> Pour les traitements 
                  basés sur le consentement (cookies non essentiels, communications marketing), vous pouvez retirer votre 
                  consentement à tout moment
                </li>
                <li>
                  <strong className="text-slate-900">Droit de définir des directives post-mortem :</strong> Définir le sort 
                  de vos données après votre décès
                </li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-6">Comment exercer vos droits ?</h3>
              <div className="bg-emerald-50 rounded-xl p-6 space-y-3">
                <p className="text-slate-700">
                  Pour exercer vos droits, contactez-nous par :
                </p>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">
                      contact@tresopilot.fr
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp Business</p>
                    <a href="tel:+33756901580" className="text-emerald-600 hover:text-emerald-700">
                      +33 7 56 90 15 80
                    </a>
                  </div>
                </div>
                <div className="border-t border-emerald-200 pt-3 mt-3">
                  <p className="font-semibold text-slate-900">Courrier postal</p>
                  <p className="text-slate-700">
                    TrésoPilot - Service Protection des Données<br />
                    60 rue François Ier<br />
                    75008 Paris<br />
                    France
                  </p>
                </div>
              </div>

              <p className="text-slate-700 leading-relaxed mt-4">
                Nous répondons à votre demande dans un délai d'<strong>un mois</strong> à compter de sa réception. 
                Ce délai peut être prolongé de deux mois si nécessaire, compte tenu de la complexité de la demande. 
                Une pièce d'identité peut être demandée pour vérifier votre identité.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                <p className="text-blue-900 font-semibold mb-2">Réclamation auprès de la CNIL</p>
                <p className="text-blue-800 leading-relaxed">
                  Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de 
                  la Commission Nationale de l'Informatique et des Libertés (CNIL) :<br />
                  <strong>www.cnil.fr</strong> • 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07
                </p>
              </div>
            </section>

            {/* 10. Sécurité */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">10. Sécurité des données</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-4">
                La protection de vos données est une priorité. Nous mettons en œuvre des mesures techniques et 
                organisationnelles appropriées pour garantir leur sécurité et leur confidentialité :
              </p>

              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li><strong>Chiffrement :</strong> Connexions HTTPS (TLS/SSL) pour toutes les communications</li>
                <li><strong>Contrôle d'accès :</strong> Authentification forte, gestion des droits d'accès, journalisation 
                des activités sensibles</li>
                <li><strong>Protection des mots de passe :</strong> Hashage sécurisé (bcrypt ou équivalent)</li>
                <li><strong>Surveillance :</strong> Détection des intrusions, monitoring des activités suspectes</li>
                <li><strong>Sauvegardes régulières :</strong> Backups automatiques avec chiffrement</li>
                <li><strong>Mise à jour :</strong> Correctifs de sécurité appliqués régulièrement</li>
                <li><strong>Formation :</strong> Sensibilisation de l'équipe aux bonnes pratiques de sécurité et de protection 
                des données</li>
              </ul>

              <p className="text-slate-700 leading-relaxed mt-4">
                Malgré ces mesures, aucun système n'est totalement infaillible. En cas de violation de données susceptible 
                d'engendrer un risque élevé pour vos droits et libertés, nous vous en informerons dans les meilleurs délais 
                conformément à la réglementation.
              </p>
            </section>

            {/* 11. Communications et rappels */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">11. Communications et rappels</h2>
              </div>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Emails transactionnels</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Nous vous envoyons des emails nécessaires au fonctionnement du Service (confirmation d'inscription, de paiement, 
                de modification d'abonnement, support). Ces emails ne nécessitent pas de consentement préalable car ils sont 
                indispensables à l'exécution du contrat.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Rappels d'échéances</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Selon votre plan d'abonnement et vos paramètres, vous pouvez recevoir des rappels par email et/ou notifications 
                in-app pour vos échéances URSSAF, TVA et CFE. Ces rappels peuvent être activés ou désactivés à tout moment dans 
                vos <strong>Paramètres</strong>.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Communications marketing</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Si vous avez consenti à recevoir nos newsletters, actualités ou offres commerciales, vous pouvez vous désinscrire 
                à tout moment via le lien de désinscription présent dans chaque email, ou en contactant le support.
              </p>
            </section>

            {/* 12. Modifications */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">12. Modifications de la politique</h2>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                Nous nous réservons le droit de modifier cette Politique de confidentialité à tout moment, notamment pour 
                refléter les évolutions de nos pratiques, de la réglementation ou de nos services.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                Toute modification substantielle vous sera notifiée par email et/ou via une notification in-app. La date de 
                dernière mise à jour est indiquée en haut de cette page.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Nous vous invitons à consulter régulièrement cette page pour rester informé de nos pratiques en matière de 
                protection des données personnelles.
              </p>
            </section>



            {/* Contact final */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Contact</h2>
              <div className="bg-emerald-50 rounded-xl p-6 space-y-3">
                <p className="text-slate-700">
                  Pour toute question concernant cette Politique de confidentialité ou l'exercice de vos droits :
                </p>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">
                      contact@tresopilot.fr
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp Business</p>
                    <a href="tel:+33756901580" className="text-emerald-600 hover:text-emerald-700">
                      +33 7 56 90 15 80
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
              <p><Tagline /></p>
              <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
              <p className="mt-2">
                Document légal RGPD — Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}